import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, withCtx as _withCtx, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = { class: "pt-2 text-lg font-medium leading-6 text-gray-900" }
const _hoisted_2 = { class: "truncate text-gray-400" }
const _hoisted_3 = /*#__PURE__*/_createElementVNode("hr", { class: "my-4" }, null, -1)
const _hoisted_4 = {
  key: 0,
  class: "mb-4"
}
const _hoisted_5 = /*#__PURE__*/_createElementVNode("hr", null, null, -1)
const _hoisted_6 = [
  _hoisted_5
]
const _hoisted_7 = { class: "pt-4 flex items-center justify-center" }
const _hoisted_8 = {
  type: "submit",
  class: "focus:outline-none inline-flex justify-center rounded-md border border-transparent bg-yellow-600 px-8 py-2 text-base font-medium text-white shadow-sm hover:bg-yellow-600 focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 sm:col-start-2 sm:text-sm"
}

import { ArrowPathIcon } from "@heroicons/vue/24/solid";
import { FieldArray, FieldEntry, Form, SubmissionHandler } from "vee-validate";
import { useI18n } from "vue-i18n";
import * as yup from "yup";
import Modal from "shared/components/modals/Modal.vue";
import { useCurrentCustomerName, useCurrentSiteId } from "shared/composables/project";
import { companyTypes } from "shared/constants/user";
import { ProjectFeature } from "shared/types/Project";
import { User, UserProject, UserProjectPermissionGroup } from "shared/types/User";
import CheckboxField from "@/components/forms/CheckboxField.vue";
import SelectListField from "@/components/forms/SelectListField.vue";
import { useCurrentProject } from "@/composables/project";
import {
  userProjectPermissionsOrdered,
  useGetSortedFeaturePermissionsForUser,
  useUpdateUserProjectPermission,
} from "@/composables/user";

type PermissionBoxEntry = {
  type: UserProjectPermissionGroup;
  enabled: boolean;
};


export default /*#__PURE__*/_defineComponent({
  __name: 'EditProjectPermissionModal',
  props: {
    user: {}
  },
  emits: ["close"],
  setup(__props: any, { emit: __emit }) {

const props = __props;
const emit = __emit;

const { t } = useI18n();

const permissionsToFeature = {
  all_camera_features_user: "camera",
  live_camera_feature_user: "camera",
  planner_user: "planner",
  processes_user: "processes",
  daily_report_user: "daily_report",
  site_activity_user: "site_activity",
} as Record<UserProjectPermissionGroup, ProjectFeature>;

const companyTypesList = companyTypes.map((type) => ({
  value: type,
  name: t(`admin.project_participants.company_types.${type}`),
}));

const currentProject = useCurrentProject();
const customerName = useCurrentCustomerName();
const siteId = useCurrentSiteId();
const { updateUserProjectPermission } = useUpdateUserProjectPermission();
const getSortedFeaturePermissionsForUser = useGetSortedFeaturePermissionsForUser();

const projectPermissionSchema = yup.object().shape({
  company_type: yup.string().required(t("authentication.validation.validation_field_required")),
  groups: yup.array().of(
    yup.object().shape({
      enabled: yup.boolean().required(),
    }),
  ),
});

const initializePermissionsForUser = (user: User) => {
  const currentFeaturesSelected = getSortedFeaturePermissionsForUser(user);
  const projectFeatures = Object.values(currentProject.features);

  const selectedPermissions = userProjectPermissionsOrdered
    .filter(
      (permission) =>
        permission === "project_admin" ||
        permission === "project_base" ||
        projectFeatures.includes(permissionsToFeature[permission]),
    )
    .map((item) => {
      return {
        type: item,
        enabled: currentFeaturesSelected.some((feat) => feat === item),
      };
    });

  const project = user.projects.find(
    (project: UserProject) => project.customer_name === customerName && project.site_id === siteId,
  );

  return {
    groups: selectedPermissions,
    company_type: (project && project.company_type) || "",
  };
};

const onCheckboxChange = (
  update: (idx: number, value: PermissionBoxEntry) => void,
  typeClicked: string,
  fields: { value: PermissionBoxEntry }[],
) => {
  for (let i = 0; i < fields.length; i++) {
    if (
      typeClicked === "live_camera_feature_user" &&
      !["live_camera_feature_user", "project_base"].includes(fields[i].value.type)
    ) {
      update(i, { type: fields[i].value.type, enabled: false });
    }
    if (
      typeClicked !== "live_camera_feature_user" &&
      fields[i].value.type === "live_camera_feature_user"
    ) {
      update(i, { type: fields[i].value.type, enabled: false });
    }
  }
};

const handleUpdateProjectPermissions: SubmissionHandler = async (genericObject) => {
  const groups = genericObject.groups
    .filter((group: PermissionBoxEntry) => group.enabled)
    .map((group: PermissionBoxEntry) => group.type);

  await updateUserProjectPermission({
    username: props.user.username,
    updatedProjectPermission: {
      customer_name: customerName,
      site_id: siteId,
      company_type: genericObject.company_type,
      explicit_groups: groups,
      groups: [],
    },
  })
    .then(() => {
      emit("close");
    })
    .catch(() => undefined);
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(Modal, {
    open: true,
    onClose: _cache[0] || (_cache[0] = ($event: any) => (emit('close'))),
    "custom-cls": "sm:w-full sm:max-w-lg"
  }, {
    title: _withCtx(() => [
      _createElementVNode("div", null, [
        _createElementVNode("h3", _hoisted_1, _toDisplayString(_ctx.user.name), 1),
        _createElementVNode("small", _hoisted_2, _toDisplayString(_ctx.user.email), 1)
      ])
    ]),
    content: _withCtx(() => [
      _createElementVNode("div", null, [
        _createVNode(_unref(Form), {
          onSubmit: handleUpdateProjectPermissions,
          initialValues: initializePermissionsForUser(_ctx.user),
          validationSchema: _unref(projectPermissionSchema),
          class: "flex flex-col gap-2 w-full"
        }, {
          default: _withCtx(() => [
            _createVNode(SelectListField, {
              name: "company_type",
              label: _unref(t)('admin.project_participants.company_type'),
              options: _unref(companyTypesList),
              minWidth: 250,
              placeholder: `--
              ${_unref(t)('form.select_option')} --`
            }, null, 8, ["label", "options", "placeholder"]),
            _hoisted_3,
            _createVNode(_unref(FieldArray), { name: "groups" }, {
              default: _withCtx(({ fields, update }) => [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(fields as FieldEntry<PermissionBoxEntry>[], (field, idx) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: idx,
                    class: "py-1"
                  }, [
                    (field.value.type === 'live_camera_feature_user')
                      ? (_openBlock(), _createElementBlock("div", _hoisted_4, _hoisted_6))
                      : _createCommentVNode("", true),
                    (field.value.type !== 'project_base')
                      ? (_openBlock(), _createBlock(CheckboxField, {
                          key: 1,
                          name: `groups[${idx}].enabled`,
                          label: _unref(t)(`permissions.${field.value.type}`),
                          onChange: ($event: any) => (
                  onCheckboxChange(
                    update,
                    field.value.type,
                    fields as FieldEntry<PermissionBoxEntry>[],
                  )
                )
                        }, null, 8, ["name", "label", "onChange"]))
                      : _createCommentVNode("", true)
                  ]))
                }), 128))
              ]),
              _: 1
            }),
            _createElementVNode("div", _hoisted_7, [
              _createElementVNode("button", _hoisted_8, [
                _createVNode(_unref(ArrowPathIcon), {
                  class: "-ml-1 mr-3 h-5 w-5",
                  "aria-hidden": "true"
                }),
                _createTextVNode(" " + _toDisplayString(_unref(t)("admin.project_participants.modal_permission.update")), 1)
              ])
            ])
          ]),
          _: 1
        }, 8, ["initialValues", "validationSchema"])
      ])
    ]),
    _: 1
  }))
}
}

})